/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDealStageLabels, type DealStage } from '@features/deals/constants/deal-stages';
import type { BrokerOption } from '../components/AttendancesFilterPanel';
import {
  BROKER_FILTER_ENABLED,
  type AttendanceFilters,
} from '../constants/attendance-filters';

/** One removable chip in the header's active-filter row. */
interface Chip {
  label: string;
  clear: () => void;
}

export interface AttendanceFiltersState {
  /** The filters to hand to `useAttendances` — all server-side params. */
  filters: AttendanceFilters;
  /** Chip labels, in the shape `PageHeader` expects. */
  activeFilters: string[];
  removeFilter: (label: string) => void;
  clearAll: () => void;
  apply: (next: AttendanceFilters) => void;
}

const STAGES_SEPARATOR = ',';

/**
 * The Atendimentos filters, held in the URL.
 *
 * The URL is the source of truth so a filtered board is a link one can
 * send someone, and so a reload does not silently widen what the user is
 * looking at.
 *
 * @param brokers - The agency's members, for resolving a chip's name.
 * @returns The current filters, the chip row, and the mutators.
 */
/**
 * One chip per selected stage; clearing a chip drops just that stage and
 * leaves the rest of the selection standing.
 */
function stageChips(
  stages: string[],
  stageLabels: Record<DealStage, string>,
  filters: AttendanceFilters,
  write: (next: AttendanceFilters) => void,
): Chip[] {
  return stages.map((stage) => ({
    label: stageLabels[stage as DealStage] ?? stage,
    clear: () => write({ ...filters, stages: stages.filter((s) => s !== stage) }),
  }));
}

/**
 * A chip row of zero or one entry, so the single-value filters compose with
 * the stage chips by spreading instead of branching at the call site.
 */
function singleChip(value: string, label: string, clear: () => void): Chip[] {
  return value ? [{ label, clear }] : [];
}

/**
 * A broker's name, falling back to the raw id when they are not in the list.
 */
function brokerName(brokers: BrokerOption[], brokerId: string): string {
  return brokers.find((b) => b.id === brokerId)?.name ?? brokerId;
}

/**
 * Use attendance filters.
 */
export function useAttendanceFilters(
  brokers: BrokerOption[] = [],
): AttendanceFiltersState {
  const { t } = useTranslation('features/attendances');
  const stageLabels = useDealStageLabels();
  const [searchParams, setSearchParams] = useSearchParams();

  const originChannel = searchParams.get('originChannel') ?? '';
  const brokerId = BROKER_FILTER_ENABLED ? (searchParams.get('brokerId') ?? '') : '';
  const rawStages = searchParams.get('stages') ?? '';

  const stages = useMemo(
    () => rawStages.split(STAGES_SEPARATOR).filter(Boolean),
    [rawStages],
  );

  const write = useCallback((next: AttendanceFilters) => {
    setSearchParams((params) => {
      const entries: Array<[string, string]> = [
        ['originChannel', next.originChannel ?? ''],
        ['stages', (next.stages ?? []).join(STAGES_SEPARATOR)],
        ['brokerId', next.brokerId ?? ''],
      ];
      entries.forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      return params;
    });
  }, [setSearchParams]);

  const filters = useMemo<AttendanceFilters>(
    () => ({ originChannel, stages, brokerId }),
    [originChannel, stages, brokerId],
  );

  const chips = useMemo<Chip[]>(() => [
    ...stageChips(stages, stageLabels, filters, write),
    ...singleChip(originChannel, t(`filters.channels.${originChannel}`), () => write({ ...filters, originChannel: '' })),
    ...singleChip(brokerId, brokerName(brokers, brokerId), () => write({ ...filters, brokerId: '' })),
  ], [stages, stageLabels, originChannel, brokerId, brokers, filters, t, write]);

  const removeFilter = useCallback((label: string) => {
    chips.find((chip) => chip.label === label)?.clear();
  }, [chips]);

  const clearAll = useCallback(() => write({}), [write]);

  return {
    filters,
    activeFilters: chips.map((chip) => chip.label),
    removeFilter,
    clearAll,
    apply: write,
  };
}
